import { useEffect, type JSX } from 'react';

import { useHubProbeStore, useHubSections, type HubSectionDef } from './hub-sections';

const ids = new WeakMap<HubSectionDef, number>();
let nextId = 0;
function identity(def: HubSectionDef): number {
  let id = ids.get(def);
  if (id === undefined) {
    nextId += 1;
    id = nextId;
    ids.set(def, id);
  }
  return id;
}

function Probe({ def }: { def: HubSectionDef }): null {
  const visible = def.useVisible ? def.useVisible() : true;
  const badge = def.useBadgeCount ? def.useBadgeCount() : 0;
  const report = useHubProbeStore((s) => s.report);
  useEffect(() => {
    report(def.key, visible, visible ? badge : 0);
  }, [report, def.key, visible, badge]);
  return null;
}

/**
 * One invisible probe per registered section, each running that section's
 * own `useVisible`/`useBadgeCount`. Keyed by the definition's identity, so a
 * re-registration mounts a fresh probe instead of changing a probe's hooks.
 */
export function HubSectionProbes(): JSX.Element {
  const sections = useHubSections();
  return (
    <>
      {sections.map((def) => (
        <Probe key={`${def.key}:${identity(def)}`} def={def} />
      ))}
    </>
  );
}
